import { supabase } from './supabase';
import { logger } from './logger';

export interface CompatibilityBreakdown {
  /** Score global 0-100, ou null si non calculable (offre sans critère). */
  score: number | null;
  skills: { matched: string[]; missing: string[]; required: number };
  sectorMatch: boolean | null;
  languages: { matched: string[]; missing: string[]; required: number };
}

export interface ApplicationDetail {
  id: string;
  status: string;
  appliedAt: string;
  viewedAt: string | null;
  selectedAt: string | null;
  candidate: {
    profileId: string;
    userId: string;
    firstName: string;
    lastName: string;
    city: string;
    photoUrl: string | null;
  };
  offer: {
    id: string;
    positionName: string;
    sectorCode: string;
    city: string;
    contractType: string;
    salaryMin: number | null;
    salaryMax: number | null;
    employerName: string;
    employerUserId: string | null;
  };
  compatibility: CompatibilityBreakdown;
}

function fmtDate(d: string | null): string | null {
  if (!d) return null;
  return new Date(d).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Détail complet d'une candidature : profil candidat, annonce, et compatibilité
 * calculée de façon transparente (compétences partagées, secteur, langues).
 */
export async function fetchApplicationDetail(applicationId: string): Promise<ApplicationDetail> {
  // 1. La candidature + identifiants liés
  const { data: app, error } = await supabase
    .from('applications')
    .select('id, status, applied_at, viewed_at, selected_at, candidate_id, job_offer_id')
    .eq('id', applicationId)
    .single();

  if (error || !app) {
    logger.error('fetch_application_detail', 'applicationService', { error: error?.message });
    throw error || new Error('Candidature introuvable');
  }

  // 2. Candidat + offre en parallèle
  const [{ data: cand }, { data: offer }] = await Promise.all([
    supabase
      .from('candidate_profiles')
      .select('id, user_id, first_name, last_name, city, photo_url')
      .eq('id', app.candidate_id)
      .single(),
    supabase
      .from('job_offers')
      .select(`
        id, position_name, sector_code, sector_id, establishment_city,
        contract_type, salary_min, salary_max, required_skills,
        employer_id, employer_profiles!job_offers_employer_id_fkey ( company_name, user_id )
      `)
      .eq('id', app.job_offer_id)
      .single(),
  ]);

  // 3. Données de compatibilité (compétences + secteurs candidat, langues des deux côtés)
  const [expRes, candLangRes, offerLangRes] = await Promise.all([
    supabase
      .from('candidate_experiences')
      .select('sector_id, candidate_experience_skills ( skill:skills ( code, name_fr ) )')
      .eq('candidate_id', app.candidate_id),
    supabase
      .from('candidate_languages')
      .select('language, level')
      .eq('candidate_id', app.candidate_id),
    supabase
      .from('job_offer_languages')
      .select('language, level')
      .eq('job_offer_id', app.job_offer_id),
  ]);

  // Compétences candidat (codes + noms)
  const candSkillCodes = new Set<string>();
  const candSkillNames = new Set<string>();
  const candSectorIds = new Set<string>();
  for (const e of (expRes.data as any[]) || []) {
    if (e.sector_id) candSectorIds.add(e.sector_id);
    for (const es of e.candidate_experience_skills || []) {
      const code = es.skill?.code;
      const name = es.skill?.name_fr;
      if (code) candSkillCodes.add(String(code).toLowerCase());
      if (name) candSkillNames.add(String(name).toLowerCase());
    }
  }

  // Compétences requises par l'offre (array de codes ou noms) → résolution des noms
  const requiredRaw: string[] = Array.isArray((offer as any)?.required_skills)
    ? ((offer as any).required_skills as any[]).map((s) => String(s))
    : [];

  let nameByCode: Record<string, string> = {};
  if (requiredRaw.length > 0) {
    const { data: skillRows } = await supabase
      .from('skills')
      .select('code, name_fr')
      .in('code', requiredRaw);
    for (const r of (skillRows as any[]) || []) nameByCode[String(r.code).toLowerCase()] = r.name_fr;
  }

  const matchedSkills: string[] = [];
  const missingSkills: string[] = [];
  for (const req of requiredRaw) {
    const reqLower = req.toLowerCase();
    const display = nameByCode[reqLower] || req;
    const isMatched =
      candSkillCodes.has(reqLower) ||
      candSkillNames.has(reqLower) ||
      candSkillNames.has((nameByCode[reqLower] || '').toLowerCase());
    if (isMatched) matchedSkills.push(display);
    else missingSkills.push(display);
  }

  // Secteur
  const offerSectorId = (offer as any)?.sector_id || null;
  const sectorMatch = offerSectorId ? candSectorIds.has(offerSectorId) : null;

  // Langues (le candidat couvre la langue avec un niveau ≥ requis)
  const candLangLevel = new Map<string, number>();
  for (const l of (candLangRes.data as any[]) || []) {
    candLangLevel.set(String(l.language).toLowerCase(), l.level || 0);
  }
  const matchedLangs: string[] = [];
  const missingLangs: string[] = [];
  for (const l of (offerLangRes.data as any[]) || []) {
    const key = String(l.language).toLowerCase();
    const have = candLangLevel.get(key);
    if (have !== undefined && have >= (l.level || 0)) matchedLangs.push(l.language);
    else missingLangs.push(l.language);
  }

  // Score pondéré (compétences 60% · secteur 25% · langues 15%)
  const reqSkillsCount = requiredRaw.length;
  const reqLangsCount = matchedLangs.length + missingLangs.length;
  let score: number | null = null;
  if (reqSkillsCount > 0 || offerSectorId || reqLangsCount > 0) {
    const skillsRatio = reqSkillsCount > 0 ? matchedSkills.length / reqSkillsCount : null;
    const sectorRatio = offerSectorId ? (sectorMatch ? 1 : 0) : null;
    const langRatio = reqLangsCount > 0 ? matchedLangs.length / reqLangsCount : null;

    let wSum = 0;
    let acc = 0;
    if (skillsRatio !== null) { acc += 0.6 * skillsRatio; wSum += 0.6; }
    if (sectorRatio !== null) { acc += 0.25 * sectorRatio; wSum += 0.25; }
    if (langRatio !== null) { acc += 0.15 * langRatio; wSum += 0.15; }
    score = wSum > 0 ? Math.round((acc / wSum) * 100) : null;
  }

  const employer = (offer as any)?.employer_profiles;

  return {
    id: app.id,
    status: app.status,
    appliedAt: fmtDate(app.applied_at) || '—',
    viewedAt: fmtDate(app.viewed_at),
    selectedAt: fmtDate(app.selected_at),
    candidate: {
      profileId: cand?.id || app.candidate_id,
      userId: cand?.user_id || '',
      firstName: cand?.first_name || '',
      lastName: cand?.last_name || '',
      city: cand?.city || '',
      photoUrl: cand?.photo_url || null,
    },
    offer: {
      id: (offer as any)?.id || app.job_offer_id,
      positionName: (offer as any)?.position_name || '',
      sectorCode: (offer as any)?.sector_code || '',
      city: (offer as any)?.establishment_city || '',
      contractType: (offer as any)?.contract_type || '',
      salaryMin: (offer as any)?.salary_min ?? null,
      salaryMax: (offer as any)?.salary_max ?? null,
      employerName: employer?.company_name || '',
      employerUserId: employer?.user_id || null,
    },
    compatibility: {
      score,
      skills: { matched: matchedSkills, missing: missingSkills, required: reqSkillsCount },
      sectorMatch,
      languages: { matched: matchedLangs, missing: missingLangs, required: reqLangsCount },
    },
  };
}
